import type { AxiosError, AxiosResponse } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import type { FormSchemaProperty, LibraryQuestion, LibrarySection } from '@/formBuilder';
import RepositoryResponse from '@/types/repositoryResponse';
import ajax from '@/utils/ajax';
import defaultRepositoryError from '@/utils/defaultRepositoryError';

export interface CreateQuestionPayload {
  key: string;
  text: string;
  definition: FormSchemaProperty;
}

export interface CreateSectionPayload {
  key: string;
  title: string;
  // Members are referenced by question id only; the section reads back each
  // member's own key, so there is nothing to name here.
  questions: { questionId: string; required: boolean }[];
}

// The library endpoints return a plain array (no pagination configured).
const asList = <T>(data: unknown): T[] =>
  Array.isArray(data) ? (data as T[]) : ((data as { results?: T[] })?.results ?? []);

export const formLibraryRepository = {
  async listQuestions({
    host,
    search
  }: {
    host: string;
    search?: string;
  }): Promise<RepositoryResponse<LibraryQuestion[]>> {
    try {
      const query = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await ajax.get({ url: `${host}/api/forms/questions/${query}` });
      return { data: asList<LibraryQuestion>(response.data), isError: false };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to load questions.');
    }
  },

  async listSections({
    host,
    search
  }: {
    host: string;
    search?: string;
  }): Promise<RepositoryResponse<LibrarySection[]>> {
    try {
      const query = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await ajax.get({ url: `${host}/api/forms/sections/${query}` });
      return { data: asList<LibrarySection>(response.data), isError: false };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to load sections.');
    }
  },

  async createQuestion({
    host,
    payload
  }: {
    host: string;
    payload: CreateQuestionPayload;
  }): Promise<RepositoryResponse<LibraryQuestion>> {
    try {
      const response = await ajax.post<CreateQuestionPayload, AxiosResponse<LibraryQuestion>>({
        url: `${host}/api/forms/questions/`,
        data: payload
      });
      return { data: response.data, isError: false };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to create question.');
    }
  },

  async createSection({
    host,
    payload
  }: {
    host: string;
    payload: CreateSectionPayload;
  }): Promise<RepositoryResponse<LibrarySection>> {
    try {
      const response = await ajax.post<CreateSectionPayload, AxiosResponse<LibrarySection>>({
        url: `${host}/api/forms/sections/`,
        data: payload
      });
      return { data: response.data, isError: false };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to create section.');
    }
  },

  async deleteQuestion({
    host,
    questionId
  }: {
    host: string;
    questionId: string;
  }): Promise<RepositoryResponse<void>> {
    try {
      await ajax.delete({ url: `${host}/api/forms/questions/${questionId}/` });
      return { data: undefined, isError: false };
    } catch (error) {
      return defaultRepositoryError(error as AxiosError, 'Failed to remove question.');
    }
  }
};

const FormLibraryRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async listQuestions(search?: string): Promise<RepositoryResponse<LibraryQuestion[]>> {
      return formLibraryRepository.listQuestions({ host, search });
    },
    async listSections(search?: string): Promise<RepositoryResponse<LibrarySection[]>> {
      return formLibraryRepository.listSections({ host, search });
    },
    async createQuestion(
      payload: CreateQuestionPayload
    ): Promise<RepositoryResponse<LibraryQuestion>> {
      return formLibraryRepository.createQuestion({ host, payload });
    },
    async createSection(
      payload: CreateSectionPayload
    ): Promise<RepositoryResponse<LibrarySection>> {
      return formLibraryRepository.createSection({ host, payload });
    },
    async deleteQuestion(questionId: string): Promise<RepositoryResponse<void>> {
      return formLibraryRepository.deleteQuestion({ host, questionId });
    }
  };
};

export type FormLibraryApi = ReturnType<typeof FormLibraryRepository>;

export default FormLibraryRepository;
